import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, CheckCircle, Clock, ArrowRightLeft, AlertCircle, Filter, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import AdminAutomationSettings from './AdminAutomationSettings';

const TYPE_COLORS = {
  workshop: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  stream: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  marketplace: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  contributor: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
};

const TYPE_LABELS = {
  workshop: 'Workshop',
  stream: 'Live Stream',
  marketplace: 'Marketplace',
  contributor: 'Contributor',
};

export default function AdminPayoutsTab() {
  const [summary, setSummary] = useState(null);
  const [pendingEarnings, setPendingEarnings] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [view, setView] = useState('pending'); // 'pending' | 'approved' | 'history'

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/payouts/summary`, { headers }),
        axios.get(`${API}/admin/payouts/pending`, { headers }),
      ]);
      setSummary(summaryRes.data);
      setPendingEarnings(pendingRes.data);
    } catch (err) {
      console.error('Failed to load payout data:', err);
      toast.error('Failed to load payout data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/payouts/history?status=${view === 'approved' ? 'approved' : 'transferred'}`, { headers });
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  }, [view]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (view !== 'pending') fetchHistory(); }, [view, fetchHistory]);

  const filtered = filterType === 'all'
    ? pendingEarnings
    : pendingEarnings.filter(e => e.type === filterType);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(e => e.id)));
    }
  };

  const handleApprove = async () => {
    if (selectedIds.size === 0) return;
    setApproving(true);
    try {
      const res = await axios.post(`${API}/admin/payouts/approve`, {
        earning_ids: Array.from(selectedIds),
      }, { headers });
      toast.success(res.data.message);
      setSelectedIds(new Set());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally {
      setApproving(false);
    }
  };

  const handleMarkTransferred = async () => {
    if (selectedIds.size === 0) return;
    setTransferring(true);
    try {
      const res = await axios.post(`${API}/admin/payouts/mark-transferred`, {
        earning_ids: Array.from(selectedIds),
      }, { headers });
      toast.success(res.data.message);
      setSelectedIds(new Set());
      fetchData();
      if (view !== 'pending') fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer marking failed');
    } finally {
      setTransferring(false);
    }
  };

  const handleReleaseViaStripe = async () => {
    if (selectedIds.size === 0) return;
    setReleasing(true);
    try {
      const res = await axios.post(`${API}/admin/payouts/release-stripe-all`, {
        earning_ids: Array.from(selectedIds),
      }, { headers });
      const { released_count, skipped_count, total_amount, results } = res.data;
      if (released_count > 0) {
        toast.success(`Released ${released_count} payout(s) totaling $${total_amount.toFixed(2)} via Stripe`);
      }
      if (skipped_count > 0) {
        const reasons = [...new Set(results.filter(r => r.status === 'skipped').map(r => r.reason))].join(', ');
        toast.warning(`${skipped_count} skipped: ${reasons}`);
      }
      if (released_count === 0 && skipped_count === 0) {
        toast.info('Nothing to release');
      }
      setSelectedIds(new Set());
      fetchData();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Stripe release failed');
    } finally {
      setReleasing(false);
    }
  };

  const selectedTotal = filtered
    .filter(e => selectedIds.has(e.id))
    .reduce((sum, e) => sum + (e.net_amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-payouts-tab">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-gray-400">Workshops</p>
              <p className="text-lg font-bold text-purple-300" data-testid="workshop-pending-total">
                ${summary.workshop_earnings.pending_total.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">{summary.workshop_earnings.pending_count} pending</p>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-gray-400">Live Streams</p>
              <p className="text-lg font-bold text-cyan-300" data-testid="stream-pending-total">
                ${summary.stream_earnings.pending_total.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">{summary.stream_earnings.pending_count} pending</p>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-gray-400">Marketplace</p>
              <p className="text-lg font-bold text-emerald-300" data-testid="vendor-pending-total">
                ${summary.vendor_earnings.pending_total.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">{summary.vendor_earnings.pending_count} pending</p>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-gray-400">Contributors</p>
              <p className="text-lg font-bold text-amber-300" data-testid="contributor-pending-total">
                ${summary.contributor_payouts.pending_total.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">{summary.contributor_payouts.pending_count} pending</p>
            </CardContent>
          </Card>
          <Card className="bg-white/5 border-teal-500/30">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-gray-400">Total Pending</p>
              <p className="text-xl font-bold text-teal-300" data-testid="grand-total-pending">
                ${summary.grand_total_pending.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">All modules</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Fee Structure Reference */}
      {summary && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex flex-wrap gap-4 text-xs text-gray-400">
              <span>Marketplace: <strong className="text-emerald-300">{summary.fee_structure.marketplace_platform_fee}</strong> platform</span>
              <span>Workshops: <strong className="text-purple-300">{summary.fee_structure.workshop_platform_fee}</strong> platform + <strong className="text-purple-300">{summary.fee_structure.workshop_processing_fee}</strong> processing</span>
              <span>Contributor Pool: <strong className="text-amber-300">{summary.fee_structure.contributor_revenue_share}</strong> of membership revenue</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Toggle & Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex bg-white/5 rounded-lg p-1 gap-1">
          {['pending', 'approved', 'history'].map(v => (
            <button
              key={v}
              onClick={() => { setView(v); setSelectedIds(new Set()); }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                view === v ? 'bg-teal-500/30 text-teal-300' : 'text-gray-400 hover:text-gray-200'
              }`}
              data-testid={`view-${v}-btn`}
            >
              {v === 'pending' ? 'Pending' : v === 'approved' ? 'Approved' : 'Transferred'}
            </button>
          ))}
        </div>

        {view === 'pending' && (
          <>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-sm">
                <Filter className="w-3 h-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="workshop">Workshops</SelectItem>
                <SelectItem value="stream">Live Streams</SelectItem>
                <SelectItem value="marketplace">Marketplace</SelectItem>
                <SelectItem value="contributor">Contributors</SelectItem>
              </SelectContent>
            </Select>

            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-gray-400">
                  {selectedIds.size} selected (${selectedTotal.toFixed(2)})
                </span>
                <Button
                  size="sm"
                  onClick={handleApprove}
                  disabled={approving}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="approve-selected-btn"
                >
                  {approving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                  Approve
                </Button>
              </div>
            )}
          </>
        )}

        {view === 'approved' && selectedIds.size > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-gray-400">{selectedIds.size} selected</span>
            <Button
              size="sm"
              onClick={handleReleaseViaStripe}
              disabled={releasing || transferring}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="release-stripe-btn"
              title="Initiate Stripe Connect transfer for the selected payouts (workshop, stream, vendor, contributor)"
            >
              {releasing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <DollarSign className="w-3 h-3 mr-1" />}
              Release via Stripe
            </Button>
            <Button
              size="sm"
              onClick={handleMarkTransferred}
              disabled={transferring || releasing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="mark-transferred-btn"
              title="Mark as manually transferred (e.g., paper check)"
            >
              {transferring ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ArrowRightLeft className="w-3 h-3 mr-1" />}
              Mark Transferred
            </Button>
          </div>
        )}
      </div>

      {/* Pending Earnings Table */}
      {view === 'pending' && (
        <Card className="bg-white/5 border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-gray-400">
                  <th className="p-3 text-left">
                    <Checkbox
                      checked={filtered.length > 0 && selectedIds.size === filtered.length}
                      onCheckedChange={selectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Practitioner</th>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-right">Gross</th>
                  <th className="p-3 text-right">Fees</th>
                  <th className="p-3 text-right">Net Payout</th>
                  <th className="p-3 text-left">Month</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-gray-500">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      No pending payouts
                    </td>
                  </tr>
                ) : (
                  filtered.map(e => (
                    <tr key={e.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="p-3">
                        <Checkbox
                          checked={selectedIds.has(e.id)}
                          onCheckedChange={() => toggleSelect(e.id)}
                          data-testid={`select-${e.id}`}
                        />
                      </td>
                      <td className="p-3">
                        <Badge className={`text-xs ${TYPE_COLORS[e.type]}`} variant="outline">
                          {TYPE_LABELS[e.type]}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="font-medium text-gray-200">{e.user_name}</div>
                        <div className="text-xs text-gray-500">{e.user_email}</div>
                      </td>
                      <td className="p-3 text-gray-300 max-w-[200px] truncate">{e.description}</td>
                      <td className="p-3 text-right text-gray-300">${(e.gross_amount || 0).toFixed(2)}</td>
                      <td className="p-3 text-right text-red-400">
                        -${((e.platform_fee || 0) + (e.processing_fee || 0)).toFixed(2)}
                      </td>
                      <td className="p-3 text-right font-semibold text-teal-300">${(e.net_amount || 0).toFixed(2)}</td>
                      <td className="p-3 text-gray-400">{e.month || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Approved / History Table */}
      {(view === 'approved' || view === 'history') && (
        <Card className="bg-white/5 border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-gray-400">
                  {view === 'approved' && <th className="p-3 text-left"><Checkbox checked={history.length > 0 && selectedIds.size === history.length} onCheckedChange={() => {
                    if (selectedIds.size === history.length) setSelectedIds(new Set());
                    else setSelectedIds(new Set(history.map(e => e.id)));
                  }} /></th>}
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Practitioner</th>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-right">Net Payout</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">{view === 'history' ? 'Transferred' : 'Approved'}</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={view === 'approved' ? 7 : 6} className="p-8 text-center text-gray-500">
                      <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      No {view === 'approved' ? 'approved' : 'transferred'} payouts
                    </td>
                  </tr>
                ) : (
                  history.map(e => (
                    <tr key={e.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      {view === 'approved' && (
                        <td className="p-3">
                          <Checkbox
                            checked={selectedIds.has(e.id)}
                            onCheckedChange={() => toggleSelect(e.id)}
                          />
                        </td>
                      )}
                      <td className="p-3">
                        <Badge className={`text-xs ${TYPE_COLORS[e.type]}`} variant="outline">
                          {TYPE_LABELS[e.type]}
                        </Badge>
                      </td>
                      <td className="p-3 text-gray-200">{e.user_name}</td>
                      <td className="p-3 text-gray-300">{e.description}</td>
                      <td className="p-3 text-right font-semibold text-teal-300">${(e.net_amount || 0).toFixed(2)}</td>
                      <td className="p-3">
                        <Badge variant="outline" className={
                          e.status === 'transferred' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                          e.status === 'approved' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                          'bg-gray-500/20 text-gray-300 border-gray-500/30'
                        }>
                          {e.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs text-gray-400">
                        {(e.transferred_at || e.approved_at || '').split('T')[0] || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Automation: monthly auto-release + daily briefing */}
      <AdminAutomationSettings />
    </div>
  );
}
